import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CardSim,
  ChevronDown,
  CircleHelp,
  Compass,
  Home,
  Moon,
  Plus,
  Sun,
  User,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { openWallet } from '../../navigation/openWallet';
import { LanguageSwitcher } from './LanguageSwitcher';

const NAV: {
  path: string;
  labelKey: string;
  match?: readonly string[];
  Icon: LucideIcon;
}[] = [
  { path: '/', labelKey: 'nav.home', Icon: Home },
  { path: '/explore', labelKey: 'nav.explore', match: ['/explore', '/country'], Icon: Compass },
  { path: '/my-esims', labelKey: 'nav.myEsims', match: ['/my-esims', '/esim'], Icon: CardSim },
  { path: '/profile', labelKey: 'nav.profile', Icon: User },
  { path: '/support', labelKey: 'nav.help', Icon: CircleHelp },
];

function isActive(pathname: string, path: string, match?: readonly string[]) {
  const prefixes = match || [path];
  return prefixes.some((p) => (p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(`${p}/`)));
}

export function DesktopNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, profile, isAuthenticated, unreadNotifications, unreadSupportChat } = useAuth();
  const { resolved, setMode } = useTheme();

  const name = user?.first_name || profile?.firstName || (isAuthenticated ? t('common.user') : t('common.guest'));
  const balance = profile?.balance ?? 0;
  const showWallet = balance > 0;

  return (
    <header className="cs-desk-navbar">
      <div className="cs-desk-navbar__inner">
        <button type="button" className="cs-desk-navbar__brand" onClick={() => navigate('/')}>
          <img
            className="cs-desk-navbar__logo"
            src="/brand/logo-64.webp"
            alt=""
            width={36}
            height={36}
            decoding="async"
          />
          <span className="cs-desk-navbar__title">CozzySIM</span>
        </button>

        <nav className="cs-desk-navbar__nav" aria-label="Main">
          {NAV.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`cs-desk-navbar__link${isActive(location.pathname, item.path, item.match) ? ' cs-desk-navbar__link--active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <span className="cs-desk-navbar__icon">
                <item.Icon size={16} strokeWidth={2} />
                {item.path === '/support' && unreadSupportChat > 0 ? (
                  <span className="cs-desk-navbar__nav-dot" aria-hidden />
                ) : null}
              </span>
              <span>{t(item.labelKey)}</span>
              {item.path === '/support' && unreadSupportChat > 0 ? (
                <span className="cs-desk-navbar__nav-count">{unreadSupportChat > 99 ? '99+' : unreadSupportChat}</span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className="cs-desk-navbar__actions">
          {showWallet ? (
            <div className="cs-desk-navbar__wallet">
              <span className="cs-desk-navbar__balance">${balance.toFixed(2)}</span>
              <span className="cs-desk-navbar__wallet-label">{t('home.walletBalance')}</span>
              <button
                type="button"
                className="cs-desk-navbar__topup"
                onClick={() =>
                  isAuthenticated ? openWallet(navigate, location) : navigate('/login?returnTo=%2Ftopup')
                }
              >
                <Plus size={14} strokeWidth={2.5} aria-hidden />
                {t('home.topUpPlus').replace(/^\+\s*/, '')}
              </button>
            </div>
          ) : null}

          <LanguageSwitcher className="cs-language-switcher--navbar" />

          <button
            type="button"
            className="cs-desk-navbar__icon-btn cs-desk-navbar__icon-btn--bell"
            aria-label={t('notifications.title')}
            onClick={() => navigate(isAuthenticated ? '/notifications' : '/login?returnTo=%2Fnotifications')}
          >
            <Bell size={16} strokeWidth={2} />
            {unreadNotifications > 0 && <span className="cs-desk-navbar__bell-dot" aria-hidden />}
          </button>

          <button
            type="button"
            className="cs-desk-navbar__icon-btn"
            aria-label={t('home.toggleTheme')}
            onClick={() => setMode(resolved === 'dark' ? 'light' : 'dark')}
          >
            {resolved === 'dark' ? <Sun size={16} strokeWidth={2} /> : <Moon size={16} strokeWidth={2} />}
          </button>

          <button
            type="button"
            className="cs-desk-navbar__user"
            onClick={() => navigate(isAuthenticated ? '/profile' : '/login?returnTo=%2Fprofile')}
          >
            {profile?.avatarUrl ? (
              <img className="cs-desk-navbar__avatar-img" src={profile.avatarUrl} alt="" />
            ) : (
              <span className="cs-desk-navbar__avatar">{name.slice(0, 1).toUpperCase()}</span>
            )}
            <span className="cs-desk-navbar__user-name">{name}</span>
            <ChevronDown className="cs-desk-navbar__chev" size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
      </div>
    </header>
  );
}
